<template>
  <div class="flex size-full overflow-hidden">
    <HeadlessTransitionRoot
      as="template"
      :show="sidebarOpen"
    >
      <HeadlessDialog
        as="div"
        class="relative z-40 lg:hidden"
        @close="sidebarOpen = false"
      >
        <HeadlessTransitionChild
          as="template"
          enter="transition-opacity ease-linear duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-gray-600/75" />
        </HeadlessTransitionChild>

        <div class="fixed inset-0 z-40 flex">
          <HeadlessTransitionChild
            as="template"
            enter="transition ease-in-out duration-300 transform"
            enter-from="-translate-x-full"
            enter-to="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leave-from="translate-x-0"
            leave-to="-translate-x-full"
          >
            <HeadlessDialogPanel
              class="relative flex w-full max-w-xs flex-1 flex-col"
            >
              <HeadlessTransitionChild
                as="template"
                enter="ease-in-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in-out duration-300"
                leave-from="opacity-100"
                leave-to="opacity-0"
              >
                <div
                  class="absolute right-0 top-0 -mr-12 pt-2"
                >
                  <button
                    type="button"
                    class="ml-1 flex size-10 items-center justify-center rounded-full text-background"
                    @click="sidebarOpen = false"
                  >
                    <span class="sr-only"
                      >Close sidebar</span
                    >
                    <IconX
                      class="size-6"
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </HeadlessTransitionChild>
              <TheSidebar :sections="sidebarSections" />
            </HeadlessDialogPanel>
          </HeadlessTransitionChild>
          <div class="w-14 shrink-0" aria-hidden="true">
            <!-- Dummy element to force sidebar to shrink to fit close icon -->
          </div>
        </div>
      </HeadlessDialog>
    </HeadlessTransitionRoot>
    <div
      class="hidden lg:inset-y-0 lg:flex lg:w-64 lg:flex-col"
    >
      <TheSidebar :sections="sidebarSections" />
    </div>
    <div
      class="z-0 mx-auto flex h-full max-w-7xl flex-1 flex-col overflow-hidden sm:px-6 lg:px-8"
    >
      <div class="flex shrink-0 border-b py-4">
        <div
          class="border-r border-background-fg/8 px-2 lg:hidden"
        >
          <ButtonWithIcon @click="sidebarOpen = true">
            <span class="sr-only">Open sidebar</span>
            <IconThreeBars
              class="size-6"
              aria-hidden="true"
            />
          </ButtonWithIcon>
        </div>
        <h1
          class="ml-4 text-xl font-medium leading-relaxed text-background-fg/87"
        >
          {{ pageTitle }}
        </h1>
      </div>
      <main
        class="flex h-full flex-1 flex-col overflow-y-auto p-4"
      >
        <RouterView />
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { SidebarSection } from "@/components/TheSidebar.vue"
import { IconAdmin, IconThreeBars, IconX } from "@/icons/"

const { pageTitle } = usePageTitle()
const route = useRoute()
const { permissions } = useAuth()
const { organizationConfig } = useOrganizationConfig()

const topLevelSections = computed<Array<SidebarSection>>(
  () =>
    permissions.value.includes("view_platform_overview")
      ? organizationConfig.value?.beepaidGoEnabled
        ? [
            { name: "dashboard" },
            { name: "beepaidGoDashboard" },
            { name: "engagementOverview" },
          ]
        : [
            { name: "dashboard" },
            { name: "engagementOverview" },
          ]
      : []
)

const merchantSection: SidebarSection = {
  name: "merchantIndex",
  children: [
    { name: "merchantIndex" },
    { name: "merchantGroupIndex" },
  ],
}

const terminalSection = computed<SidebarSection | null>(
  () =>
    permissions.value.includes("view_terminals") ||
    permissions.value.includes("bulk_unpair_terminals")
      ? {
          name: "terminalIndex",
          children: [
            ...(permissions.value.includes("view_terminals")
              ? [{ name: "terminalIndex" }]
              : []),
            ...(permissions.value.includes(
              "bulk_unpair_terminals"
            )
              ? [{ name: "bulkUnpairTerminals" }]
              : []),
          ],
        }
      : null
)

const userSection = computed<SidebarSection | null>(() =>
  permissions.value.includes("view_users") ||
  permissions.value.includes("manage_users")
    ? {
        name: "userIndex",
        children: [
          { name: "userIndex" },
          ...(permissions.value.includes("manage_users")
            ? [{ name: "roleIndex" }]
            : []),
        ],
      }
    : null
)

const notificationSection = computed<SidebarSection | null>(
  () =>
    permissions.value.includes("manage_notifications")
      ? {
          name: "notificationIndex",
          children: [{ name: "notificationIndex" }],
        }
      : null
)

const sidebarSections = computed<Array<SidebarSection>>(
  () => [
    ...topLevelSections.value,
    merchantSection,
    ...(terminalSection.value
      ? [terminalSection.value]
      : []),
    ...(userSection.value ? [userSection.value] : []),
    ...(notificationSection.value
      ? [notificationSection.value]
      : []),
    ...(permissions.value.includes("access_admin")
      ? [
          {
            href: "/control/admin",
            label: "Staff Admin",
            icon: IconAdmin,
            showAtBottom: true,
          },
        ]
      : []),
  ]
)

const sidebarOpen = ref(false)

watch(
  () => route.fullPath,
  () => {
    sidebarOpen.value = false
  }
)
</script>
